import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "I need a modular kitchen",
  "Wardrobe ideas for a small bedroom",
  "What services do you offer?",
];

const GREETING =
  "Hello — I'm the STAR AVG design assistant. Tell me about your space (room, size, style you like) and I'll share some ideas.";

const getSessionId = () => {
  let id = localStorage.getItem("sae_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("sae_chat_session", id);
  }
  return id;
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    sessionRef.current = getSessionId();
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (preset) => {
    const msg = (preset ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const resp = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      if (!resp.ok || !resp.body) throw new Error("chat failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") continue;
          try {
            const payload = JSON.parse(data);
            if (payload.delta) {
              acc += payload.delta;
              const current = acc;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: current };
                return copy;
              });
            } else if (payload.error) {
              acc = payload.error;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: acc };
                return copy;
              });
            }
          } catch {
            /* partial chunk */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Sorry, I'm unavailable right now. Please use the enquiry form below or WhatsApp us at +91 95828 87899.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-button"
        aria-label="Open design assistant chat"
        onClick={() => setOpen(!open)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.4, duration: 0.5 }}
        className="fixed bottom-6 left-6 z-50 flex h-14 w-14 items-center justify-center rounded-full border border-line bg-paper text-ink transition-colors duration-300 hover:bg-clay"
      >
        {open ? <X size={20} /> : <Sparkles size={20} />}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-4 z-50 flex h-[60vh] w-[calc(100vw-2rem)] max-w-sm flex-col border border-line bg-paper sm:left-6"
          >
            <div className="border-b border-line px-5 py-4">
              <p className="font-serif text-lg tracking-tight">Design assistant</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-taupe">
                Powered by Gemini · ideas only, no quotes
              </p>
            </div>
            <div ref={listRef} data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-ink text-paper" : "bg-sand text-ink"
                    }`}
                  >
                    {m.text || <Loader2 size={14} className="animate-spin text-taupe" />}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => send(s)}
                      className="border border-line px-3 py-2 text-xs text-taupe transition-colors duration-300 hover:border-ink hover:text-ink"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t border-line p-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="flex items-center gap-3"
              >
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Describe your space..."
                  maxLength={1000}
                  className="flex-1 border-b border-line bg-transparent py-2 text-sm outline-none transition-colors duration-300 focus:border-ink placeholder:text-taupe/60"
                />
                <button
                  type="submit"
                  data-testid="chat-send-button"
                  disabled={busy || !input.trim()}
                  aria-label="Send message"
                  className="text-ink transition-colors duration-300 hover:text-clay disabled:opacity-40"
                >
                  <Send size={18} />
                </button>
              </form>
              <button
                data-testid="chat-enquiry-link"
                onClick={() => {
                  setOpen(false);
                  scrollToId("#contact");
                }}
                className="mt-3 text-[10px] uppercase tracking-[0.25em] text-taupe transition-colors duration-300 hover:text-ink"
              >
                Ready to talk? Send us an enquiry
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
